import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "csm_cart_v1";
const DEV = typeof process !== "undefined" && process.env.NODE_ENV !== "production";

function readStorage() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    // localStorage can be unavailable (iOS private mode, disabled cookies)
    // or the JSON may be corrupted. Start with an empty cart rather than
    // crashing — the cart is non-essential UI state.
    if (DEV) {
      // eslint-disable-next-line no-console
      console.warn("Cart: readStorage fallback", err);
    }
    return [];
  }
}

export function CartProvider({ children }) {
  // Lazy initial state hydrates synchronously from localStorage so the
  // first commit already has the persisted cart — this prevents the
  // double-effect race that would otherwise wipe storage on mount.
  const [items, setItems] = useState(() => readStorage());

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      // Quota exceeded or private mode — the cart survives in memory for this
      // session; next reload it resets. Non-critical.
      if (DEV) {
        // eslint-disable-next-line no-console
        console.warn("Cart: writeStorage failed", err);
      }
    }
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      const stockCap = Math.max(1, product.stock || 1);
      if (existing) {
        const next = Math.min(stockCap, existing.qty + qty);
        return prev.map((i) => (i.product_id === product.id ? { ...i, qty: next } : i));
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name_es: product.name_es,
          name_en: product.name_en,
          price: product.price,
          image: product.image_url,
          qty: Math.min(stockCap, qty),
          bottle_count: product.bottle_count || 1,
          stock: product.stock,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((product_id, qty) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.product_id === product_id
            ? { ...i, qty: Math.max(1, Math.min(i.stock || 99, qty)) }
            : i
        )
        .filter((i) => i.qty > 0)
    );
  }, []);

  const removeItem = useCallback((product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const totalBottles = items.reduce((s, i) => s + (i.bottle_count || 1) * i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const count = items.reduce((s, i) => s + i.qty, 0);
    return { totalBottles, subtotal: Math.round(subtotal * 100) / 100, count };
  }, [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQty, removeItem, clearCart, totals }),
    [items, addItem, updateQty, removeItem, clearCart, totals]
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
